import {LitElement, css, html, nothing} from "lit";
import {Directive, directive} from "lit/directive.js";
import {repeat} from "lit/directives/repeat.js";
import {MIN_YEAR, MAX_YEAR} from "./profile-finance-grid.js";

class FormatKPI extends Directive {

	render(value, isPercent) {
		if(value === null)
			return html`-`;

		if(isPercent)
			return html`${value + "%"}`;

		return html`${value}`;
	}

}

export const formatKPI = directive(FormatKPI);

export class ProfileFinanceGridKPI extends LitElement {

	static get properties() {
		return {
			profileID: { type: String, attribute: "profile-id", reflect: true },
			loading: { type: Boolean, attribute: false },
			error: { type: String, attribute: false },
			year: { type: Number, attribute: false },
			columns: { type: Array, attribute: false },
			data: { type: Array, attribute: false }
		};
	}

	constructor() {
		super();

		/**
		 * Map list of already loaded years.
		 *
		 * {Year: true}
		 */
		this._loadedYears = {};

		/**
		 * Next rows loading indicator. (Not for changing year view)
		 *
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * The error message when something went wrong and the grid can no longer show valid data.
		 *
		 * @type {string|null}
		 */
		this.error = null;

		/**
		 * The end cursor of the cursor pagination.
		 *
		 * @type {string|null}
		 */
		this._endCursor = null;

		/**
		 * Indicates if further rows exists, and a loadNext is allowed.
		 *
		 * @type {boolean} Defaults to true for the initial load.
		 */
		this._hasNextPage = true;

		/**
		 * The initial year.
		 *
		 * The columns and rows render depending on this value.
		 *
		 * The columns are composed out of this value: (year - 2) & (year) & (year + 2) which are 5 years in total, with
		 * the current year in the center.
		 *
		 * @type {number}
		 */
		this.year = new Date().getFullYear();

		// Initial set of the columns
		this.updateColumns();

		/**
		 * The rows data.
		 * The year keys/properties of the row is the data for the column cell which is expected to be always present.
		 *
		 * [{Name: "Row Name", 2018: null, 2019: "2.00", 2020: null, ...}, ...]
		 */
		this.data = [];
	}

	attributeChangedCallback(name, old, value) {
		super.attributeChangedCallback(name, old, value);

		switch(name) {
			case "profile-id":
				if(old !== undefined)
					this.reset();
				if(value)
					requestAnimationFrame(() => this.loadNext());
				break;
		}
	}

	reset() {
		this._loadedYears = {};
		this.year = new Date().getFullYear();
		this.updateColumns();
		this._endCursor = null;
		this._hasNextPage = true;
		this.error = null;
		this.loading = false;
		this.data = [];
	}

	refresh() {
		this._loadedYears = {};
		this._endCursor = null;
		this._hasNextPage = true;
		this.error = null;
		this.loading = false;
		this.data = [];
		requestAnimationFrame(() => this.loadNext());
	}

	/**
	 * Changes the view of the years by adding 1.
	 */
	nextYear() {
		// Because the table is always showing 5 years, and this.year is the middle one, we have to add 3 to check
		// the most right year not to exceed the limit.
		if(this.year+3 > MAX_YEAR)
			return;

		this.dispatchEvent(new CustomEvent("profile-finance-grid-year-change", {
			composed: true,
			detail: {
				target: this,
				direction: "next"
			}
		}));

		this.year++;
		this.updateColumns();
		this.load(this.columns[this.columns.length-1]);
	}

	/**
	 * Changes the view of the years by subtracting 1.
	 */
	prevYear() {
		// Because the table is always showing 5 years, and this.year is the middle one, we have to subtract 3 to check
		// the most left year not to exceed the limit.
		if(this.year-3 < MIN_YEAR)
			return;

		this.dispatchEvent(new CustomEvent("profile-finance-grid-year-change", {
			composed: true,
			detail: {
				target: this,
				direction: "prev"
			}
		}));

		this.year--;
		this.updateColumns();
		this.load(this.columns[0]);
	}

	/**
	 * Loads the next rows.
	 */
	loadNext() {
		if(this._hasNextPage)
			this.load(this.columns[0], this.columns[this.columns.length-1]);
	}

	/**
	 * Updates the viewed column years.
	 */
	updateColumns() {
		this.columns = [this.year - 2, this.year - 1, this.year, this.year + 1, this.year + 2];
	}

	/**
	 * Loads the table data.
	 *
	 * The next rows will be loaded when fromYear and toYear is provided.
	 *
	 * Only the data for the column year is loaded when only fromYear is provided.
	 */
	load(fromYear, toYear = null) {
		this.error = null;

		if(!this.profileID) {
			this.error = "No Profile ID defined. " + this.profileID;
			return;
		}

		const query = `
			query ProfileKPIValues($profileID: ID!, $fromYear: Int!, $toYear: Int, $first: Int, $after: String) {
				listKPI(first: $first, after: $after) {
					edges {
						node {
							Name
							Type
							isPercent
							ProfileValues(profileID: $profileID, fromYear: $fromYear, toYear: $toYear, fillYears: true) {
								Year
								Value
							}
						}
					}
					pageInfo {
						endCursor
						hasNextPage
					}
				}
			}
		`;

		const variables = {
			profileID: this.profileID,
			first: 10,
			fromYear: fromYear,
			toYear: toYear,
			fillYears: true
		};

		if(toYear !== null) {
			// Next loading
			this.loading = true;

			if(this._endCursor !== null) {
				variables.after = this._endCursor;

				// Reset loaded years, because if we load the next rows, we only have the loaded years of the
				// previous years and would not see the values of the new rows when changing the year view.
				this._loadedYears = {};
			}
		} else {
			// Skip, because year is already loaded.
			if(this._loadedYears.hasOwnProperty(fromYear))
				return;

			// Partial year update, load all years of previously loaded rows
			variables.first = this.data.length;
		}

		fetch("/graphql", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Accept": "application/json"
			},
			body: JSON.stringify({
				query: query,
				variables: variables
			})
		}).then(r => r.json())
		.then(response => {
			if(!response || response.errors) {
				this.error = "Data Error:" + JSON.stringify(response);
				return;
			}

			const edges = response.data.listKPI.edges;
			if(edges.length === 0)
				return;

			if(toYear !== null) {
				this._endCursor = response.data.listKPI.pageInfo.endCursor;
				this._hasNextPage = response.data.listKPI.pageInfo.hasNextPage;

				// Next load of entries, add to table
				this.data.push(...edges.map(item => {
					const kpi = item.node;

					// Turns years in ProfileValues object as keys in the kpi variable.
					const years = {};
					kpi.ProfileValues.forEach(kpiValue => {
						years[kpiValue.Year] = kpiValue.Value !== null ? kpiValue.Value : null;

						// Cache year as loaded
						this._loadedYears[kpiValue.Year] = true;
					});

					Object.assign(kpi, years);

					delete kpi.ProfileValues;

					return kpi;
				}));
			} else {
				// Partial update, just update/insert entries of the year
				for(let i in this.data) {
					const kpiEntry = this.data[i];

					for(let i2 in edges) {
						const kpi = edges[i2].node;

						if(kpi.Type === kpiEntry.Type) {
							// Turns years in ProfileValues object as keys in the kpi variable.
							kpi.ProfileValues.forEach(kpiValue => {
								this.data[i][kpiValue.Year] = kpiValue.Value !== null ? kpiValue.Value : null;

								// Cache year as loaded
								this._loadedYears[kpiValue.Year] = true;
							});
							break;
						}
					}
				}
			}

			// Update DOM
			this.requestUpdate();
		})
		.catch(reason => this.error = reason)
		.finally(() => this.loading = false);
	}

	static get styles() {
		return css`
			:host {
				display: block;
			}
			
			.container {
				padding-bottom: 16px;
			}
			
			.grid-table {
				display: grid;
				grid-template-columns: 2fr repeat(5, 1fr);
			}
			
			/* Affects all first column cells (the first and every 6th item) */
			.grid-table > :nth-child(6n+1) {
				text-align: left;
				border-left: none;
			}
			
			.grid-table > div {
				display: flex;
				flex-direction: column;
			    justify-content: center;
			    padding: 6px 24px;
			    border-bottom: 1px solid var(--divider-color);
			    border-left: 1px solid var(--divider-color);
			    text-align: center;
			}
			
			.column {
				position: relative;
				padding: 16px 24px;
				
				/* --paper-font-body2 */
				font-size: 14px;
		        font-weight: 500;
		        line-height: 24px;
			}
			
			.column button {
				position: absolute;
				background: transparent;
				height: calc(100% - 6px);
				margin: 0px 3px;
				padding: 0;
				border: 1px solid transparent;
				border-radius: 3px;
				color: var(--primary-color);
			}
			
			.column button:focus {
				border: 1px solid var(--primary-color);
				outline: none;
			}
			
			.column button:hover {
				background: var(--divider-color);
			}
			
			.column button:active {
				background: var(--primary-color);
				color: #FFF;
			}
			
			.column.prev button {
				left: 0;
			}
			
			.column.next button {
				right: 0;
			}
			
			.cell {			
				/* Hack */
				--paper-input-container_-_padding: 0px;
				
				/* --paper-font-body1 */
				font-size: 14px;
		        font-weight: 400;
		        line-height: 20px;
			}
			
			.cell.value {
				padding: 6px;
			}
			
			.cell paper-spinner-lite {
				align-self: center;
				width: 16px;
				height: 16px;
				--paper-spinner-stroke-width: 2px;
			}
			
			.loading {
				display: flex;
				flex-direction: column;
				padding: 16px;
				border-bottom: 1px solid var(--divider-color);
			}
			
			.loading > paper-spinner-lite {
				align-self: center;
			}
			
			.loading > .error {
				color: var(--error-color);
			}
		`;
	}

	render() {
		return html`
            <div class="container">
                <section class="grid-table">
                    <!-- Columns -->
                    <div class="column">Name ${this.hasChanged ? "*" : nothing}</div>
                     ${this.columns.map((year, index) => {
                     	if(index === 0)
                     		return this._prevColumn(year);
                     	if(index === this.columns.length-1)
                     		return this._nextColumn(year);
                     	return html`<div class="column">${year}</div>`;
					})}

                    <!-- Rows -->
	                ${repeat(this.data, kpi => kpi.Type, kpi => html`
						<div class="cell">${kpi.Name}${kpi.isPercent ? " (%)" : nothing}</div>
						${this.columns.map(year => this._cell(kpi, year))}
					`)}
                </section>
                
                ${(this.loading || this.error !== null) ? html`
                    <div class="loading">
						${this.loading ? html`<paper-spinner-lite active></paper-spinner-lite>` : nothing}
						${this.error !== null ? html`<div class="error">Error: ${this.error}</div>` : nothing}
					</div>
                ` : nothing}
            </div>
		`;
	}

	_prevColumn(year) {
		return html`
			<div class="column prev">
				${this.data.length > 0 && this.year-3 >= MIN_YEAR ? html`
				<button @click="${this.prevYear}">
					<paper-ripple></paper-ripple>
					<iron-icon icon="chevron-left"></iron-icon>
				</button>
				` : nothing}
				
				<div>${year}</div>
			</div>
		`;
	}

	_nextColumn(year) {
		return html`
			<div class="column next">
				${this.data.length > 0 && this.year+3 <= MAX_YEAR ? html`
				<button @click="${this.nextYear}">
					<paper-ripple></paper-ripple>
					<iron-icon icon="chevron-right"></iron-icon>
				</button>
				` : nothing}
				
				<div>${year}</div>
			</div>
		`;
	}

	_cell(kpi, year) {
		return html`<div class="cell value">${this._loadedYears[year] ? html`${formatKPI(kpi[year], kpi.isPercent)}` : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>`;
	}

}

customElements.define("profile-finance-grid-kpi", ProfileFinanceGridKPI);